import { useState, useEffect, useMemo } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import type { SprintListItem } from '../../services/sprintService';
import { resolveIssueTypeConfig } from '../../constants/issueTypeConfig';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  sprint: SprintListItem | null;
  allSprints: SprintListItem[];
  onComplete: (carryOverToSprintId?: string) => void;
  isSubmitting: boolean;
}

export default function SprintCompleteModal({
  isOpen,
  onClose,
  sprint,
  allSprints,
  onComplete,
  isSubmitting,
}: Props) {
  const [nextSprintId, setNextSprintId] = useState('');

  const incompleteIssues = useMemo(() => {
    if (!sprint) return [];
    return sprint.items.filter(
      (item) => item.issue.workflowStatus.category !== 'DONE',
    );
  }, [sprint]);

  const plannedSprints = useMemo(
    () => allSprints.filter((s) => s.status === 'PLANNED'),
    [allSprints],
  );

  // 기본 선택: 첫 번째 PLANNED 스프린트
  useEffect(() => {
    if (isOpen && plannedSprints.length > 0) {
      setNextSprintId(plannedSprints[0].sprintId);
    } else {
      setNextSprintId('');
    }
  }, [isOpen, plannedSprints]);

  if (!isOpen || !sprint) return null;

  const handleComplete = () => {
    onComplete(nextSprintId || undefined);
  };

  // 미완료 작업이 없는 경우 간단한 확인
  if (incompleteIssues.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">스프린트 종료</h3>
            <button onClick={onClose} disabled={isSubmitting} className="p-1 hover:bg-gray-100 rounded-lg">
              <X size={18} className="text-gray-500" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            모든 작업이 완료되었습니다. &ldquo;{sprint.name}&rdquo;을 종료하시겠습니까?
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              취소
            </button>
            <button
              onClick={handleComplete}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-50"
            >
              {isSubmitting ? '처리 중...' : '스프린트 종료'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">스프린트 종료</h3>
          <button onClick={onClose} disabled={isSubmitting} className="p-1 hover:bg-gray-100 rounded-lg">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          {/* 경고 */}
          <div className="flex items-center gap-2 text-orange-600 text-sm bg-orange-50 px-3 py-2 rounded-lg">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>완료되지 않은 작업 {incompleteIssues.length}건</span>
          </div>

          <p className="text-sm text-gray-600">
            종료되지 않은 작업 항목은 다음 스프린트에 자동으로 이관됩니다.
          </p>

          {/* 미완료 작업 목록 */}
          <div className="max-h-[200px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
            {incompleteIssues.map((item) => {
              const typeConfig = resolveIssueTypeConfig(item.issue.issueTypeDef, item.issue.issueType);
              const ws = item.issue.workflowStatus;
              return (
                <div
                  key={item.sprintItemId}
                  className="flex items-center gap-2 px-3 py-2 text-sm"
                >
                  {typeConfig && (
                    <typeConfig.Icon size={12} className={`shrink-0 ${typeConfig.color}`} />
                  )}
                  <span className="font-mono text-sm text-gray-400 shrink-0">
                    {item.issue.issueId}
                  </span>
                  <span className="truncate flex-1 text-gray-800">
                    {item.issue.title}
                  </span>
                  {ws && (
                    <span
                      className="text-[11px] px-1.5 py-0.5 rounded shrink-0 text-white"
                      style={{ backgroundColor: ws.color }}
                    >
                      {ws.name}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* 이관 대상 스프린트 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              이관 대상 스프린트
            </label>
            {plannedSprints.length > 0 ? (
              <select
                value={nextSprintId}
                onChange={(e) => setNextSprintId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {plannedSprints.map((s) => (
                  <option key={s.sprintId} value={s.sprintId}>
                    {s.name}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                다음 스프린트가 없습니다. 새 스프린트가 자동 생성됩니다.
              </p>
            )}
          </div>
        </div>

        {/* 버튼 */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleComplete}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            스프린트 종료
          </button>
        </div>
      </div>
    </div>
  );
}
